import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { DashboardQueryProps, EarningsByService } from "@customTypes/dashboard";
import { Pagination } from "@customTypes/index";

import { dashboardQKeys } from "./queryKeys";


const select = (res: any) => {
  //   console.log(res.data);
  const pagination: Pagination = {
    page: res.data.pagination.page,
    pageCount: res.data.pagination.page_count,
    pageSize: res.data.pagination.page_size,
    total: res.data.pagination.total,
  };
  const newData = [] as EarningsByService[];

  const totals = {
    totalAmountToBePaid: res.data.total_amount_paid_in || 0,
    totalAmountDueToBePaidOut: res.data.total_amount_due_to_staff || 0,
    totalAmountPaidOut: res.data.total_amount_paid_out || 0,
    totalProcessingPayout: res.data.total_processing_payout || 0,
    totalOutstandingPayout: res.data.total_outstanding_payout || 0,
    totalPendingReportCompletion: res.data.total_pending_report_completion || 0,
  };

  if (res.data.services.length) {
    for (const i in res.data.services) {
      const {
        payment_id,
        bill_id,
        date_of_service,
        consultation_id,
        consultation_record_id,
        bill_status,
        report_status,
        amount_paid_in,
        amount_due_to_staff,
        service_id,
        service_name,
        service_category,
        staff_name,
        staff_id,
        commission_percentage,
        commission_percentage_source,
        payout_status,
        patient_id,
      } = res.data.services[i];
      newData.push({
        paymentId: payment_id,
        billId: bill_id,
        dateOfService: new Date(date_of_service),
        staffName: staff_name,
        staffId: staff_id,
        patientId: patient_id,
        cId: consultation_id,
        consultationId: consultation_record_id,
        serviceId: service_id,
        serviceName: service_name || service_category || "-",
        serviceCategory: service_category || "-",
        billStatus: bill_status?.toLocaleLowerCase(),
        reportStatus: report_status?.toLocaleLowerCase(),
        amountPaidIn: amount_paid_in,
        amountDue: amount_due_to_staff,
        commissionPercentage: commission_percentage ?? null,
        commissionPercentageSource: commission_percentage_source,
        payoutStatus: payout_status?.toLocaleLowerCase(),
      });
    }
    return { newData, pagination, totals };
  }

  return { newData: null, pagination, totals };
};

interface QueryProps extends DashboardQueryProps {
  billStatus?: string;
  reportStatus?: string;
  payoutStatus?: string;
}

export const useGetEarningsByServices = ({
  dateRange,
  startDate,
  endDate,
  pagination,
  search,
  billStatus,
  reportStatus,
  payoutStatus,
  userId,
}: QueryProps) => {
  const handleErrors = useQueryErrorHandling();

  return useQuery({
    queryKey: dashboardQKeys.earningsByServicesWithFilters({
      dateRange,
      startDate,
      endDate,
      pagination,
      ...(search && { search }),
      ...(userId && { userId }),
      ...(billStatus && { billStatus }),
      ...(reportStatus && { reportStatus }),
      ...(payoutStatus && { payoutStatus }),
    }),
    queryFn: () => {
      return api.get("analytics/earnings/services", {
        params: {
          range: dateRange?.replaceAll("-", "_"),
          ...(dateRange === "custom" && {
            start_date: startDate,
            end_date: endDate,
          }),
          page: pagination?.page,
          page_size: pagination?.pageSize,
          ...(search && { search }),
          ...(userId && { staff_id: userId }),
          ...(billStatus && { bill_status: billStatus }),
          ...(reportStatus && { report_status: reportStatus }),
          ...(payoutStatus && { payout_status: payoutStatus }),
        },
      });
    },
    select,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled:
      dateRange !== "custom" ||
      !!(dateRange === "custom" && startDate && endDate),

    onError: (res: any) => {
      console.error("[From Get Earnings By Services]", res);
      handleErrors(res, "query");
    },
  });
};
